// Phase 44.08 — Order Timeline (customer order detail).
// Verticale, minimal, premium (Uber Eats / Apple Wallet style).
// Deriva i passi dall'order doc — nessuna chiamata API aggiuntiva.
//
// Stati per step:
//   completed  → dot emerald, check, line above filled
//   current    → dot blue glow, label bold
//   future     → dot zinc, label muted
//   failed     → dot red (per cancelled)
//
// Auto-open quando ordine in-progress. Collapsed by default su delivered/cancelled.
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Check, Clock, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { formatDateTime } from "@/lib/format";

function fmtTs(iso) {
  if (!iso) return null;
  try {
    return formatDateTime(iso);
  } catch { return null; }
}

// Costruisce i passi visibili dato lo stato dell'ordine.
function buildSteps(order, ratings) {
  if (!order) return [];
  const status = order.status;
  const isCancelled = status === "cancelled";
  const isDelivered = status === "delivered";

  // status progress index (per derivare completed/current/future)
  const progress = {
    pending: 0,
    accepted: 1,
    picking_up: 2,
    picked_up: 3,
    delivering: 4,
    delivered: 5,
    cancelled: -1,
  };
  const stepIdx = progress[status] ?? 0;

  // Mappa: per ogni step, calcola stato in base a stepIdx
  // step index references:
  //   0: ordine creato (sempre completed)
  //   1: pagamento (solo se serve)
  //   2: driver assegnato (status >= accepted)
  //   3: pacco ritirato (status >= picked_up)
  //   4: in viaggio (status >= picked_up, current se in picked_up/delivering)
  //   5: driver arrivato a destinazione (status >= delivering)
  //   6: consegnato (status === delivered)
  //   7: valutazione inviata (se rating customer esiste)
  const stateFor = (requiredStatus, isCurrent) => {
    // Cancelled: solo lo step "Consegnato" sarà override-ato in 'failed' qui sotto.
    // Gli step intermedi non avvenuti restano 'future' (no rosso fuorviante).
    if (isCancelled) {
      // Se quello step ha un timestamp reale (es. picked_up_at su un ordine
      // poi cancellato dopo il ritiro), mantienilo 'completed'.
      return stepIdx >= progress[requiredStatus] ? "completed" : "future";
    }
    if (stepIdx >= progress[requiredStatus]) {
      if (isCurrent && !isDelivered) return "current";
      return "completed";
    }
    return "future";
  };

  const out = [];

  // 1) Ordine creato
  out.push({
    key: "created",
    label: "Ordine creato",
    timestamp: fmtTs(order.created_at),
    state: isCancelled && stepIdx < 0 ? "completed" : "completed",
  });

  // 2) Pagamento ricevuto — solo se pagato realmente con carta (non sub/pack)
  const paid = order.payment_status === "paid";
  const showPay = paid && (order.payment_source === "stripe_single" || !order.payment_source);
  if (showPay) {
    out.push({
      key: "paid",
      label: "Pagamento ricevuto",
      timestamp: fmtTs(order.paid_at),
      state: "completed",
    });
  }

  // 3) Driver assegnato
  const hasDriver = !!order.driver_id;
  out.push({
    key: "assigned",
    label: "Driver assegnato",
    sublabel: hasDriver ? order.driver_name : null,
    timestamp: fmtTs(order.accepted_at),
    state: isCancelled
      ? (hasDriver ? "completed" : "future")
      : hasDriver
        ? (stepIdx === 1 ? "current" : "completed")
        : "future",
  });

  // 4) Pacco ritirato — quando stepIdx >= 3 è "completed" (l'evento è avvenuto).
  //    Lo step "In viaggio" qui sotto è quello che mostra il "current".
  out.push({
    key: "picked_up",
    label: "Pacco ritirato",
    timestamp: fmtTs(order.picked_up_at),
    state: isCancelled
      ? (order.picked_up_at ? "completed" : "future")
      : (stepIdx >= 3 ? "completed" : "future"),
  });

  // 5) In viaggio
  out.push({
    key: "in_transit",
    label: "In viaggio verso il destinatario",
    state: stepIdx >= 3
      ? (stepIdx < 5 ? "current" : "completed")
      : isCancelled ? "future" : "future",
  });

  // 6) Driver arrivato a destinazione
  const drvArrivedAt = order.recipient_notif_sent?.driver_arrived_at;
  out.push({
    key: "driver_arrived",
    label: "Driver arrivato a destinazione",
    timestamp: fmtTs(drvArrivedAt),
    state: stateFor("delivering", stepIdx === 4),
  });

  // 7) Consegnato
  out.push({
    key: "delivered",
    label: "Consegnato",
    timestamp: fmtTs(order.delivered_at),
    state: isCancelled ? "failed" : (isDelivered ? "completed" : "future"),
  });

  // 8) Valutazione inviata (solo se rating customer esiste)
  const customerRating = (ratings || []).find((r) => r.from_role === "customer");
  if (isDelivered && customerRating) {
    out.push({
      key: "rated",
      label: "Hai inviato la tua valutazione",
      timestamp: fmtTs(customerRating.created_at),
      state: "completed",
    });
  }

  // Override: se cancelled, marca lo step "Consegnato" come failed con label custom
  if (isCancelled) {
    const last = out[out.length - 1];
    if (last && last.key === "delivered") {
      last.label = "Ordine annullato";
      last.timestamp = fmtTs(order.cancelled_at);
      last.state = "failed";
    }
  }
  return out;
}

const STATE_STYLES = {
  completed: { dot: "bg-emerald-500", ring: "ring-emerald-100", line: "bg-emerald-300/60", icon: Check, iconCls: "text-white" },
  current:   { dot: "bg-dapnow-blue", ring: "ring-dapnow-blue/20 ring-4 animate-pulse", line: "bg-zinc-200", icon: Clock, iconCls: "text-white" },
  future:    { dot: "bg-zinc-300", ring: "ring-zinc-100", line: "bg-zinc-200", icon: null, iconCls: "" },
  failed:    { dot: "bg-red-500", ring: "ring-red-100", line: "bg-red-200", icon: X, iconCls: "text-white" },
};

export default function OrderTimelineCard({ order, ratings }) {
  // Auto-open quando ordine in-progress, chiusa di default su delivered/cancelled.
  const isActive = order && !["delivered", "cancelled"].includes(order.status);
  const [open, setOpen] = useState(isActive);
  const didTrackRef = useRef(false);

  // Adatta open quando lo status cambia da in-progress a delivered/cancelled
  useEffect(() => {
    if (!isActive && open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status]);

  // Track open one-shot per mount
  useEffect(() => {
    if (open && !didTrackRef.current && order?.id) {
      didTrackRef.current = true;
      trackEvent("timeline_opened", { order_id: order.id, status: order.status });
    }
  }, [open, order?.id, order?.status]);

  const toggle = () => {
    if (open) trackEvent("timeline_collapsed", { order_id: order?.id, status: order?.status });
    setOpen((o) => !o);
  };

  const steps = buildSteps(order, ratings);
  const completedCount = steps.filter((s) => s.state === "completed").length;
  const totalCount = steps.length;
  const hasFailed = steps.some((s) => s.state === "failed");

  return (
    <section
      className="card-soft overflow-hidden"
      data-testid="order-timeline-card"
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="order-timeline-toggle"
        className="w-full px-5 sm:px-6 py-4 flex items-center justify-between gap-3 active:bg-zinc-50/60 transition"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue shrink-0">
            <Clock className="h-4 w-4" />
          </span>
          <div className="text-left min-w-0">
            <p className="font-display text-base font-bold tracking-tight leading-tight">
              Timeline ordine
            </p>
            <p className="text-[11px] text-dapnow-muted mt-0.5">
              {hasFailed
                ? "Ordine annullato"
                : `${completedCount} di ${totalCount} eventi completati`}
            </p>
          </div>
        </div>
        {open
          ? <ChevronUp className="h-5 w-5 text-dapnow-muted shrink-0" />
          : <ChevronDown className="h-5 w-5 text-dapnow-muted shrink-0" />}
      </button>

      {open && (
        <ol
          className="px-5 sm:px-6 pb-5 pt-1 space-y-0"
          data-testid="order-timeline-list"
        >
          {steps.map((step, i) => {
            const styles = STATE_STYLES[step.state] || STATE_STYLES.future;
            const Icon = styles.icon;
            const isLast = i === steps.length - 1;
            const nextState = !isLast ? (steps[i + 1].state) : null;
            // line color: legata allo stato del prossimo
            const lineCls = nextState === "completed"
              ? "bg-emerald-300/60"
              : nextState === "current"
                ? "bg-gradient-to-b from-emerald-300/60 to-dapnow-blue/40"
                : nextState === "failed"
                  ? "bg-red-200"
                  : "bg-zinc-200";

            return (
              <li
                key={step.key}
                className="relative pl-9 pb-4 last:pb-0"
                data-testid={`timeline-step-${step.key}`}
                data-state={step.state}
              >
                {!isLast && (
                  <span className={`absolute left-[14px] top-7 bottom-0 w-px ${lineCls}`} aria-hidden />
                )}
                <span
                  className={`absolute left-0 top-1 inline-flex h-7 w-7 items-center justify-center rounded-full ring-4 ${styles.ring} ${styles.dot}`}
                >
                  {Icon ? <Icon className={`h-3.5 w-3.5 ${styles.iconCls}`} strokeWidth={3} /> : null}
                </span>
                <div className="min-h-[28px] flex items-start gap-3 justify-between">
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm leading-tight ${
                        step.state === "current"
                          ? "font-bold text-dapnow-ink"
                          : step.state === "completed"
                            ? "font-semibold text-dapnow-ink"
                            : step.state === "failed"
                              ? "font-bold text-red-700"
                              : "text-dapnow-muted"
                      }`}
                    >
                      {step.label}
                    </p>
                    {step.sublabel && (
                      <p className="text-[11px] text-dapnow-muted mt-0.5 truncate">{step.sublabel}</p>
                    )}
                    {step.timestamp && (
                      <p className="text-[11px] text-dapnow-muted mt-0.5 tabular-nums">{step.timestamp}</p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
